// Central registry of dashboard modules. Adding a new module = append here +
// map its (moduleKey, tab) pair to a component in App.tsx's dispatcher.
//
// The ticket-069 ModuleSwitcher uses this list to render the dropdown; the
// Layout uses the matched module's `sidebarItems` to paint the nav.
//
// Keep this file declarative — no React components, no runtime logic. That
// keeps the circular-import surface small (registry.ts imports icons,
// components import registry).
//
// Marketing is split per ticket-070 decision #2 into sub-modules (mkt-meta,
// mkt-ai, mkt-google, mkt-video, mkt-landing, mkt-chatbot, mkt-analytics).
// Most marketing submodules are `comingSoon` because their pages ship in
// Track E follow-ups. WhatsApp is top-level per decision #3.

import { WhatsAppGlyph } from '@/modules/whatsapp-app/components/WhatsAppGlyph';
import {
  Activity,
  Images,
  BarChart3,
  BookOpen,
  Brain,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  Camera,
  Clock,
  ClipboardList,
  Filter,
  Layers,
  Contact,
  Database,
  FileText,
  Film,
  Folder,
  FolderOpen,
  Globe,
  Headphones,
  Inbox,
  LayoutTemplate,
  Eye,
  LineChart,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  Mic,
  Music,
  Package,
  Palette,
  Phone,
  Plane,
  Plus,
  Receipt,
  Send,
  Settings,
  Share2,
  Shield,
  ShoppingCart,
  Sparkles,
  Star,
  Stethoscope,
  Tag,
  Upload,
  UserCheck,
  UserCircle,
  Users,
  Video,
  Wand2,
  Zap,
  Radar,
  GraduationCap,
  SlidersHorizontal, Satellite, MapPin, GitMerge, Grid3x3, ShieldCheck,
  BadgeDollarSign, Percent, Store, Wallet, Boxes, ArrowLeftRight, CalendarClock, Gauge, ClipboardCheck, AlertOctagon, ListTodo, Target, TrendingUp, Bell, LayoutGrid, CircleDollarSign, HeartPulse, ClipboardPlus, Landmark, Route, Trees, Cctv, PawPrint } from 'lucide-react';

import type { ModuleDefinition } from './types';

// ── Iconify helpers ───────────────────────────────────────────
const size = 16;

// ── Registry ──────────────────────────────────────────────────

export const moduleRegistry: ModuleDefinition[] = [
  // ═══ Ventas ════════════════════════════════════════════════
  {
    key: 'travel-sales',
    name: 'Ventas — Agencia de Viajes',
    category: 'ventas',
    icon: <Plane size={size} />,
    backendModuleKey: 'travel-admin',
    sidebarItems: [
      { tab: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={size} /> },
      { tab: 'bookings', label: 'Reservaciones', icon: <ShoppingCart size={size} /> },
      { tab: 'trips', label: 'Viajes', icon: <Plane size={size} /> },
      { tab: 'categories', label: 'Categorías', icon: <Tag size={size} /> },
      { tab: 'plans', label: 'Planes', icon: <Package size={size} /> },
      { tab: 'addons', label: 'Addons', icon: <Star size={size} /> },
      { tab: 'blogs', label: 'Blog', icon: <BookOpen size={size} /> },
      { tab: 'authors', label: 'Autores', icon: <UserCheck size={size} /> },
    ],
  },

  // ═══ Bookings 1:1 — coaches/personal-brand (NABU-71) ═══════════
  {
    key: 'bookings-1to1',
    name: 'Reservas 1:1',
    category: 'ventas',
    byArchetype: {
      'healthcare-clinic': { name: 'Citas Médicas', category: 'consultorio' }, 'events-services': { name: 'Agenda de eventos', category: 'panel' },
      // Universidades B2B (futuria/mondragon): la misma agenda que el
      // médico pero en clave admisiones — pedido Samuel 2026-07-04.
      'educational-institution': {
        name: 'Agenda de Admisiones',
        category: 'operaciones',
        sidebarItems: {
          list: { label: 'Citas' },
          profiles: { label: 'Perfiles' },
        },
      },
    },
    icon: <Calendar size={size} />,
    backendModuleKey: 'booking',
    sidebarItems: [
      { tab: 'list', label: 'Reservas', icon: <Calendar size={size} /> },
      { tab: 'profiles', label: 'Perfiles', icon: <UserCheck size={size} /> },
    ],
  },

  // ═══ Empresa — top-level (incluye Negocios como sub-tab) ═════
  {
    key: 'empresa',
    name: 'Empresa',
    category: 'config',
    byArchetype: { 'healthcare-clinic': { name: 'Mi clínica', category: 'config' }, 'events-services': { name: 'Mi organización', category: 'config' }, 'government': { name: 'Institución', category: 'config' } },
    icon: <Building2 size={size} />,
    backendModuleKey: null,
    requireAdmin: true,   // administración de la empresa — no para empleados
    // 🏛️ Per-org: en municipio-slp la parte de sistema (Configuración/Institución)
    // la administra el operador, no el cliente → se oculta el módulo completo.
    // Fail-open: cualquier otra org lo sigue viendo igual. (Después se evaluará
    // habilitar edición del prompt para gobierno.)
    hiddenForOrgs: ['municipio-slp'],
    sidebarItems: [
      { tab: 'company', label: 'Configuración', icon: <Settings size={size} /> },
      { tab: 'ai-agent', label: 'Agente IA', icon: <Bot size={size} /> },
      { tab: 'notificaciones', label: 'Notificaciones', icon: <Bell size={size} /> },
      { tab: 'marketing-safety', label: 'Seguridad Marketing', icon: <Shield size={size} /> },
      { tab: 'negocios', label: 'Negocios', icon: <Building2 size={size} /> },
      // Prender/apagar módulos de la org (admin/cto; el backend lo exige).
      { tab: 'modulos', label: 'Módulos', icon: <LayoutGrid size={size} />, requireAdmin: true },
    ],
  },

  // ═══ Google — app propia (Cuenta · Calendario · Google Ads) ═══
  // Calendar se conecta por PERSONA (mcp_django #498; scope google_calendar:*
  // también para operador/vendedor), por eso no lleva backendModuleKey. Google
  // Ads es por org y de admin: su pestaña pide admin y el módulo marketing
  // (lo revisa la vista).
  {
    key: 'google',
    name: 'Google',
    category: 'operaciones',
    icon: <Globe size={size} />,
    // Apagable por org con la key `google` de /org/modules (tolerante: mientras
    // el backend no la reporte, se ve como siempre). Nunca en orgs soberanas:
    // conectar Google Calendar/Ads es enviar datos a un servicio extranjero.
    softBackendModuleKey: 'google',
    hideForSovereign: true,
    sidebarItems: [
      { tab: 'cuenta', label: 'Cuenta', icon: <Globe size={size} /> },
      { tab: 'calendario', label: 'Calendario', icon: <CalendarDays size={size} /> },
      { tab: 'ads', label: 'Google Ads', icon: <Megaphone size={size} />, requireAdmin: true },
    ],
  },

  // ═══ Marketing (Marketing OS · F1) ═════════════════════════
  // Una sola app de marketing por org: gasto vs presupuesto, costo por lead y
  // por venta por canal y campaña, y salud de la medición. Contrato de API en
  // /root/docs/67-marketing-os-agentico.md §6. Convive con los mkt-* (mismo
  // módulo backend) hasta F4, cuando estos se muden adentro.
  {
    key: 'marketing',
    name: 'Marketing',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Marketing', category: 'captacion' }, 'events-services': { name: 'Marketing', category: 'captacion' }, 'service-pro': { name: 'Marketing', category: 'captacion' } },
    icon: <TrendingUp size={size} />,
    backendModuleKey: 'marketing',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'resumen', label: 'Resumen', icon: <BarChart3 size={size} /> },
      { tab: 'presupuesto', label: 'Presupuesto', icon: <Wallet size={size} /> },
      { tab: 'medicion', label: 'Medición', icon: <Gauge size={size} /> },
      // Marketing Studio (mcp_django #571): bandeja de aprobación y ajustes por org.
      { tab: 'por-aprobar', label: 'Por aprobar', icon: <ClipboardCheck size={size} /> },
      { tab: 'ajustes', label: 'Ajustes', icon: <SlidersHorizontal size={size} /> },
    ],
  },

  // ═══ Marketing — sub-modules ═══════════════════════════════
  {
    key: 'mkt-meta',
    name: 'Marketing — Meta',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Anuncios Meta/IG', category: 'captacion' }, 'events-services': { name: 'Anuncios Meta/IG', category: 'captacion' } , 'service-pro': { name: 'Marketing — Meta', category: 'captacion' } },
    icon: <Megaphone size={size} />,
    backendModuleKey: 'marketing',
    sidebarItems: [
      { tab: 'dashboard', label: 'Overview', icon: <BarChart3 size={size} /> },
      { tab: 'leads', label: 'Leads', icon: <Mail size={size} /> },
      { tab: 'publish', label: 'Publicar', icon: <Send size={size} />, requirePublish: true },
      { tab: 'social-publish', label: 'Calendario de posts', icon: <ClipboardList size={size} /> },
      { tab: 'approve', label: 'Aprobar drafts', icon: <ClipboardList size={size} /> },
      { tab: 'campaigns', label: 'Campañas', icon: <Activity size={size} /> },
      { tab: 'director', label: 'Director IA', icon: <Sparkles size={size} /> },
      { tab: 'boost', label: 'Boost posts', icon: <Zap size={size} /> },
    ],
  },
  {
    key: 'mkt-ai',
    name: 'Marketing — IA',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'IA de captación', category: 'captacion' }, 'events-services': { name: 'IA de captación', category: 'captacion' } , 'service-pro': { name: 'Marketing — IA', category: 'captacion' } },
    icon: <Sparkles size={size} />,
    backendModuleKey: 'marketing',
    sidebarItems: [
      { tab: 'content', label: 'Contenido IA', icon: <Sparkles size={size} /> },
      { tab: 'gallery-images', label: 'Galería de imágenes', icon: <Camera size={size} /> },
      { tab: 'audience', label: 'Audiencias IA', icon: <Users size={size} /> },
      { tab: 'alerts', label: 'Alertas / Optimizer', icon: <Activity size={size} /> },
    ],
  },
  {
    key: 'mkt-video',
    name: 'Marketing — Video',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Videos promocionales', category: 'captacion' }, 'events-services': { name: 'Videos promocionales', category: 'captacion' } , 'service-pro': { name: 'Marketing — Video', category: 'captacion' } },
    icon: <Video size={size} />,
    backendModuleKey: 'marketing',
    sidebarItems: [
      { tab: 'gallery', label: 'Galería', icon: <Video size={size} /> },
      { tab: 'templates', label: 'Templates', icon: <LayoutTemplate size={size} /> },
    ],
  },
  {
    key: 'heygen',
    name: 'HeyGen — Video AI',
    category: 'marketing',
    icon: <Film size={size} />,
    backendModuleKey: 'heygen',
    requireCto: true,
    sidebarItems: [
      { tab: 'video-generator', label: 'Generar video', icon: <Video size={size} /> },
      { tab: 'studio', label: 'Studio', icon: <Film size={size} /> },
      { tab: 'hg-templates', label: 'Templates', icon: <LayoutTemplate size={size} /> },
      { tab: 'translate', label: 'Traducir', icon: <Globe size={size} /> },
      { tab: 'hg-jobs', label: 'Jobs', icon: <ClipboardList size={size} /> },
    ],
  },
  {
    key: 'mkt-google',
    name: 'Marketing — Google Ads',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Anuncios Google', category: 'captacion' }, 'events-services': { name: 'Anuncios Google', category: 'captacion' } , 'service-pro': { name: 'Marketing — Google Ads', category: 'captacion' } },
    icon: '🟢',
    backendModuleKey: 'marketing',
    sidebarItems: [
      { tab: 'dashboard', label: 'Conexión', icon: <BarChart3 size={size} /> },
      { tab: 'campaigns', label: 'Campañas', icon: <Activity size={size} /> },
    ],
  },
  {
    key: 'mkt-landing',
    name: 'Marketing — Landing Pages',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Landing Pages', category: 'captacion' }, 'events-services': { name: 'Landing Pages', category: 'captacion' } , 'service-pro': { name: 'Marketing — Landing Pages', category: 'captacion' } },
    icon: <LayoutTemplate size={size} />,
    backendModuleKey: 'marketing',
    comingSoon: true,
    sidebarItems: [
      { tab: 'pages', label: 'Landing pages', icon: <LayoutTemplate size={size} />, comingSoon: true },
      { tab: 'domains', label: 'Dominios', icon: <Globe size={size} />, comingSoon: true },
    ],
  },
  {
    key: 'mkt-chatbot',
    name: 'Marketing — Chatbot',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Chatbot del sitio', category: 'captacion' }, 'events-services': { name: 'Chatbot del sitio', category: 'captacion' } , 'service-pro': { name: 'Marketing — Chatbot', category: 'captacion' } },
    icon: <Bot size={size} />,
    backendModuleKey: 'marketing',
    sidebarItems: [
      { tab: 'flows', label: 'Flows', icon: <Bot size={size} /> },
      { tab: 'sessions', label: 'Sesiones', icon: <Inbox size={size} /> },
    ],
  },
  {
    key: 'mkt-analytics',
    name: 'Marketing — Analytics',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Métricas web', category: 'captacion' }, 'events-services': { name: 'Métricas web', category: 'captacion' } , 'service-pro': { name: 'Marketing — Analytics', category: 'captacion' } },
    icon: <LineChart size={size} />,
    backendModuleKey: 'marketing',
    sidebarItems: [
      { tab: 'overview', label: 'Overview', icon: <LineChart size={size} /> },
      { tab: 'kpis', label: 'KPIs Meta', icon: <LineChart size={size} /> },
      { tab: 'alerts', label: 'Alertas', icon: <LineChart size={size} /> },
    ],
  },

  // ═══ WhatsApp ════════════════════════════════════════════
  // El antiguo módulo «Difusión WhatsApp» (key 'whatsapp': inbox, contactos,
  // plantillas, broadcasts) se integró a la app «WhatsApp» (key 'whatsapp-app',
  // pestaña Difusión) el 2026-10-22. Sus URLs /m/whatsapp/* redirigen allá
  // (routes/ModuleRoute.tsx LEGACY_REDIRECTS). El módulo contratable del backend
  // `whatsapp` sigue mandando: decide si Difusión ofrece Contactos y Envíos.

  // ═══ Automations ═══════════════════════════════════════════
  {
    key: 'automations',
    name: 'Automatizaciones',
    category: 'operaciones',
    byArchetype: { 'healthcare-clinic': { name: 'Recordatorios automáticos', category: 'pacientes' }, 'events-services': { name: 'Recordatorios automáticos', category: 'clientes' } },
    icon: <Zap size={size} />,
    backendModuleKey: 'automations',
    sidebarItems: [
      { tab: 'flows', label: 'Flows', icon: <Zap size={size} /> },
    ],
  },

  // ═══ Sitio público (apariencia + constructor) ══════════════
  {
    key: 'site',
    name: 'Sitio Web',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Sitio web clínica', category: 'captacion' }, 'events-services': { name: 'Sitio web', category: 'captacion' } , 'service-pro': { name: 'Sitio Web', category: 'captacion' } },
    icon: <Globe size={size} />,
    backendModuleKey: 'site_config', // always visible to admins for now
    sidebarItems: [
      { tab: 'editor', label: 'Editor del sitio', icon: <Eye size={size} /> },
      { tab: 'builder', label: 'Constructor', icon: <LayoutTemplate size={size} /> },
      { tab: 'theme', label: 'Apariencia', icon: <Palette size={size} /> },
    ],
  },

  // ═══ Correo corporativo ════════════════════════════════════
  {
    key: 'mail',
    name: 'Correo',
    category: 'operaciones',
    byArchetype: { 'healthcare-clinic': { name: 'Correo a pacientes', category: 'pacientes' }, 'events-services': { name: 'Correo a clientes', category: 'clientes' } , 'service-pro': { name: 'Correo', category: 'clientes' } },
    icon: <Inbox size={size} />,
    backendModuleKey: 'email',
    sidebarItems: [
      { tab: 'mailboxes', label: 'Buzones', icon: <Inbox size={size} /> },
    ],
  },

  // ═══ Recruitment (solo cuando la org lo tiene habilitado) ══
  {
    key: 'recruitment',
    name: 'Reclutamiento',
    category: 'rh',
    icon: <UserCheck size={size} />,
    backendModuleKey: 'recruitment',
    sidebarItems: [
      { tab: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={size} /> },
      { tab: 'vacancies', label: 'Vacantes', icon: <ClipboardList size={size} /> },
      { tab: 'candidates', label: 'Candidatos', icon: <Users size={size} /> },
    ],
  },

  // ═══ Voz / llamadas (solo cuando la org lo tiene habilitado) ══
  {
    key: 'voice',
    name: 'Voz y llamadas',
    category: 'operaciones',
    byArchetype: { 'healthcare-clinic': { name: 'Llamadas', category: 'pacientes' }, 'events-services': { name: 'Llamadas', category: 'clientes' } , 'service-pro': { name: 'Voz y llamadas', category: 'clientes' } },
    icon: <Phone size={size} />,
    backendModuleKey: 'voice',
    sidebarItems: [
      { tab: 'voice', label: 'Voice chat', icon: <Phone size={size} /> },
      { tab: 'calls', label: 'Llamadas masivas', icon: <Phone size={size} /> },
      { tab: 'call-history', label: 'Historial', icon: <ClipboardList size={size} /> },
    ],
  },
  {
    key: 'elevenlabs',
    name: 'ElevenLabs — Audio AI',
    category: 'operaciones',
    icon: <Headphones size={size} />,
    backendModuleKey: 'elevenlabs',
    requireCto: true,
    sidebarItems: [
      { tab: 'tts', label: 'Text-to-Speech', icon: <Mic size={size} /> },
      { tab: 'podcast', label: 'Podcast', icon: <Headphones size={size} /> },
      { tab: 'sfx-music', label: 'SFX / Música', icon: <Music size={size} /> },
      { tab: 'tools', label: 'Herramientas', icon: <Wand2 size={size} /> },
      { tab: 'el-jobs', label: 'Jobs', icon: <ClipboardList size={size} /> },
    ],
  },

  // ═══ CRM / Leads ═══════════════════════════════════════════
  {
    key: 'crm',
    name: 'CRM',
    category: 'operaciones',
    byArchetype: { 'healthcare-clinic': { name: 'Pacientes', category: 'consultorio' }, 'events-services': { name: 'Clientes', category: 'clientes' } , 'service-pro': { name: 'CRM', category: 'clientes' } },
    icon: <Contact size={size} />,
    backendModuleKey: 'crm',
    sidebarItems: [
      { tab: 'leads', label: 'Leads', icon: <Mail size={size} /> },
      { tab: 'contacts', label: 'Contactos', icon: <Contact size={size} /> },
      { tab: 'bulk', label: 'Envío masivo', icon: <Send size={size} /> },
      { tab: 'history', label: 'Historial', icon: <ClipboardList size={size} /> },
    ],
  },

  // ═══ Dev-only ═══════════════════════════════════════════════
  {
    key: 'dev',
    name: 'Dev / Internals',
    category: 'dev',
    icon: <Database size={size} />,
    backendModuleKey: 'dev_agent',
    devOnly: true,
    sidebarItems: [
      { tab: 'backend', label: 'Backend data', icon: <Database size={size} />, devOnly: true },
      { tab: 'dev-tasks', label: 'Dev tasks', icon: <ClipboardList size={size} />, devOnly: true },
      { tab: 'my-threads', label: 'My threads', icon: <MessageSquare size={size} />, devOnly: true },
      { tab: 'customers', label: 'Customers', icon: <Users size={size} />, devOnly: true },
      { tab: 'twilio', label: 'Twilio', icon: <Phone size={size} />, devOnly: true },
    ],
  },

  // ═══ Config — always visible, no module gate ═══════════════
  {
    key: 'config',
    name: 'Configuración',
    category: 'config',
    icon: <Settings size={size} />,
    backendModuleKey: null,
    requireAdmin: true,   // configuración del sistema — no para empleados
    // 🏛️ Per-org: en municipio-slp la configuración del sistema (Guía/Workflows/
    // Prompts) la administra el operador. Fail-open: otras orgs sin cambio.
    // (Pendiente: evaluar exponer SOLO la pestaña Prompts para gobierno.)
    hiddenForOrgs: ['municipio-slp'],
    sidebarItems: [
      { tab: 'guide', label: 'Guía de uso', icon: <BookOpen size={size} /> },
      { tab: 'workflows', label: 'Workflows', icon: <Zap size={size} /> },
      { tab: 'prompts', label: 'Prompts', icon: <Settings size={size} /> },
      // NOTE: 'Proveedores LLM' (TKT-97) vive embebido en Empresa → API keys
      // (TravelAdmin.tsx → CompanyThirdPartyKeys → <LLMProvidersSection />)
      // junto a las keys HEYGEN/ELEVENLABS/FAL. No duplicar como tab aquí.
    ],
  },

  // ═══ Time Tracking — registro horas + bulk-bill (NABU-65) ════════
  // Cierra el gap projects→invoicing: timer en vivo + tabla weekly +
  // conversión a line items facturables. Bow-tie: backend usa
  // BigIntegerField nullable para project_id/billed_invoice_id.
  {
    key: 'time-tracking',
    name: 'Time Tracking',
    category: 'operaciones',
    icon: <Clock size={size} />,
    backendModuleKey: 'time_tracking',
    sidebarItems: [
      { tab: 'dashboard', label: 'Esta semana', icon: <ClipboardList size={size} /> },
      { tab: 'summary', label: 'Summary', icon: <BarChart3 size={size} /> },
    ],
  },

  // ═══ Documentos — Drive multi-tenant per-org (TKT-144 Ola 1) ══════
  // Subir/listar/compartir docs con visibilidad personal/org/shared/public.
  // Permission resolver STRICT anti-leak en backend (apps/documents/
  // permissions.py). North star: portal médico-paciente, abogado-cliente.
  // Ver /root/docs/Arquitectura/03-archetypes-y-modulos.md.
  {
    key: 'documents',
    name: 'Documentos',
    category: 'operaciones',
    byArchetype: { 'healthcare-clinic': { name: 'Expedientes', category: 'consultorio' }, 'events-services': { name: 'Cotizaciones y documentos', category: 'panel' } },
    icon: <FileText size={size} />,
    backendModuleKey: 'documents',
    sidebarItems: [
      { tab: 'mine',   label: 'Mis documentos',     icon: <Folder size={size} /> },
      { tab: 'shared', label: 'Compartidos conmigo', icon: <Share2 size={size} /> },
      { tab: 'org',    label: 'De la organización', icon: <FolderOpen size={size} /> },
      { tab: 'upload', label: 'Subir nuevo',        icon: <Upload size={size} /> },
    ],
  },

  // ═══ Cockpit clínico — MEDOS-16 ═════════════════════════════════
  // Agenda del médico + lista de pacientes con expediente.
  // Visible solo cuando archetype org = healthcare-clinic (futuro
  // MEDOS-14); por ahora visible con backendModuleKey='clinical' que
  // Orion mapea al archetype service-pro/healthcare-clinic.
  {
    key: 'clinical-cockpit',
    name: 'Consultorio',
    category: 'operaciones',
    byArchetype: {
      'healthcare-clinic': { name: 'Consultorio', category: 'consultorio' },
      'events-services': {
        name: 'Panel',
        category: 'panel',
        sidebarItems: {
          agenda: { label: 'Agenda' },
          patients: { label: 'Clientes' },
        },
      },
    },
    icon: <Stethoscope size={size} />,
    backendModuleKey: 'clinical',
    sidebarItems: [
      { tab: 'home',     label: 'Inicio',     icon: <Activity size={size} /> },
      { tab: 'agenda',   label: 'Mi agenda',  icon: <CalendarDays size={size} /> },
      { tab: 'patients', label: 'Pacientes',  icon: <UserCircle size={size} /> },
    ],
  },
  {
    // Bazares y Expositores — para quien ORGANIZA eventos donde terceros rentan
    // espacio (bazares, ferias, expos). Backend: apps/exhibitions.
    // ⚠️ MÓDULO PER-ORG: `backendModuleKey` lo hace fail-CLOSED — NO aparece en
    // ninguna org hasta que se encienda en su `enabled_modules`. NO meterlo en
    // ARCHETYPE_DEFAULT_MODULES: mimercadito comparte archetype 'agency-travel'
    // con calaita, que NO organiza bazares.
    key: 'bazares',
    name: 'Bazares',
    category: 'operaciones',
    icon: <Store size={size} />,
    backendModuleKey: 'bazares',
    sidebarItems: [
      { tab: 'ventas', label: 'Ventas por bazar', icon: <CalendarDays size={size} /> },
      { tab: 'stands', label: 'Stands', icon: <Store size={size} /> },
      { tab: 'expositores', label: 'Expositores', icon: <Users size={size} /> },
      { tab: 'cobrar', label: 'Por cobrar', icon: <Wallet size={size} /> },
      { tab: 'material', label: 'Material', icon: <Images size={size} /> },
    ],
  },

  {
    // Diagnóstico Operativo con IA (IA Organizational Scan) — lo contesta CADA
    // EMPLEADO por el chat; el agente conduce y va guardando por bloques.
    // 🚨 Sin esta entrada el backend habilitaba `org-scan` pero el frontend no
    // tenía dónde ponerlo: el empleado caía en `empresa` y veía
    // "Insufficient permissions". Ver components/orgscan/EntrevistaPage.tsx.
    key: 'org-scan',
    name: 'Diagnóstico Operativo',
    category: 'operaciones',
    icon: <ClipboardList size={size} />,
    backendModuleKey: 'org-scan',
    sidebarItems: [
      { tab: 'entrevista', label: 'Mi diagnóstico', icon: <ClipboardList size={size} /> },
    ],
  },

  {
    // Diagnósticos (mcp_django apps/diagnostics): programas (interno / clientes /
    // captación) → proyectos → participantes, invitaciones, análisis, reporte,
    // resultado del dueño y embudo. Producto transversal y multi-tenant: la org
    // sale del JWT en el backend. Gateado por backendModuleKey 'diagnostics'
    // (Orión ALL_MODULES, default OFF en todo archetype → se enciende por org en
    // enabled_modules). Solo staff.
    // ADR-007 (F1): lo usa el rol `consultor`, no solo el admin → `requireRoles`
    // (admin/dev pasan siempre; el resto necesita `consultor`). El candado real es
    // el backend (scopes diagnostics:* y, en otra empresa, el engagement).
    // «Mis empresas»: empresas del SO a las que la consultora entra por engagement.
    key: 'diagnosticos',
    name: 'Diagnósticos',
    category: 'operaciones',
    audiences: ['staff'],
    requireRoles: ['consultor'],
    icon: <ClipboardCheck size={size} />,
    backendModuleKey: 'diagnostics',
    sidebarItems: [
      { tab: 'programas', label: 'Programas', icon: <Layers size={size} /> },
      { tab: 'proyectos', label: 'Proyectos', icon: <ClipboardList size={size} /> },
      { tab: 'empresas', label: 'Mis empresas', icon: <Building2 size={size} /> },
      { tab: 'embudo', label: 'Embudo', icon: <Filter size={size} /> },
    ],
  },

  {
    // Foco (mcp_django apps/focus): pendientes que administra el agente — PM de
    // IA para directivos. Captura por WhatsApp, «qué me toca hoy», resumen
    // matutino y revisión semanal. Genérico y por org; primer uso onepercent.
    // Gateado por backendModuleKey 'focus' (default OFF en todo archetype).
    key: 'focus',
    name: 'Foco',
    category: 'operaciones',
    audiences: ['staff'],
    icon: <Target size={size} />,
    backendModuleKey: 'focus',
    sidebarItems: [
      { tab: 'tablero', label: 'Tablero', icon: <Target size={size} /> },
    ],
  },

  {
    // Seguimiento de rendimiento (apps/performance de mcp_django): check-in
    // diario, carga, sparring, hábitos y semáforo de preparación. Genérico;
    // primer uso javier-mma. Los roles atleta/entrenador/tutor ven SÓLO este
    // módulo (lo fuerza el backend en /org/modules, como el asociado de Galán).
    key: 'performance',
    name: 'Mi tablero',
    category: 'operaciones',
    audiences: ['staff'],
    icon: <Activity size={size} />,
    backendModuleKey: 'performance',
    sidebarItems: [
      { tab: 'tablero', label: 'Mi tablero', icon: <Activity size={size} /> },
    ],
  },

  {
    // Galán y Asociados — "Primera línea": triage IA de bandeja alto volumen.
    key: 'galan-inbox',
    name: 'Primera línea',
    category: 'operaciones',
    // Audiencias: staff (asociados de Galán) + partner (vendedoras de Calaita).
    // Cada quien ve SU propio correo. El gate real de qué orgs lo tienen sigue
    // siendo backendModuleKey+isEnabled (hoy Galán y Calaita); la audiencia solo
    // dice qué PORTAL puede verlo. Un vendedor de una org sin galan-inbox
    // encendido no lo ve → cero blast radius.
    audiences: ['staff', 'partner'],
    byArchetype: {
      // Galán (despacho): "Correo Inteligente" — bandeja priorizada por IA.
      'service-pro': {
        name: 'Correo Inteligente',
        category: 'operaciones',
        sidebarItems: { inbox: { label: 'Correo Inteligente' } },
      },
      // Reuso para agencias de viajes: la misma bandeja, sin la marca de Galán.
      'agency-travel': {
        name: 'Mi Correo',
        category: 'operaciones',
        sidebarItems: { inbox: { label: 'Mi Correo' } },
      },
    },
    icon: <Inbox size={size} />,
    backendModuleKey: 'galan-inbox',
    sidebarItems: [
      { tab: 'inbox', label: 'Primera línea', icon: <Inbox size={size} /> },
    ],
  },

  {
    // Portal del Cliente — audience 'client'. El cliente externo del tenant
    // (rol cliente_final) ve SOLO este módulo: estado de sus casos, saldo de
    // su bono de horas, cuánto debe, documentos y contacto con su abogado.
    // Multi-tenant: cualquier org con clientes lo hereda (backend lo devuelve
    // solo a roles cliente). Datos gateados por org+identidad en el backend.
    key: 'clients-portal',
    name: 'Portal del Cliente',
    category: 'panel',
    icon: <Briefcase size={size} />,
    audiences: ['client'],
    backendModuleKey: 'clients_portal',
    sidebarItems: [
      { tab: 'home', label: 'Mi caso', icon: <Briefcase size={size} /> },
      { tab: 'cuenta', label: 'Estado de cuenta', icon: <Receipt size={size} /> },
      { tab: 'bono', label: 'Bono de horas', icon: <Clock size={size} /> },
      { tab: 'documentos', label: 'Documentos', icon: <FileText size={size} /> },
      { tab: 'contacto', label: 'Mi abogado', icon: <Phone size={size} /> },
    ],
  },

  {
    // Leads del agente telefónico (CALL CENTER IA) — genérico, cualquier org
    // con call center. Gateado por backendModuleKey 'phone-leads' en
    // enabled_modules. Lee Orión /orion/api/leads/phone (tabla phone_leads).
    key: 'phone-leads',
    name: 'Leads',
    category: 'operaciones',
    byArchetype: { 'events-services': { name: 'Leads', category: 'panel' } },
    icon: <Phone size={size} />,
    backendModuleKey: 'phone-leads',
    sidebarItems: [
      { tab: 'leads', label: 'Leads', icon: <Phone size={size} /> },
    ],
  },

  {
    // Obra Radar — radar de demanda constructiva para rentalmaq (renta de maquinaria).
    // Gateado: backendModuleKey 'obra-radar' (solo en enabled_modules de rentalmaq) +
    // el proxy de Orión exige org_id==rentalmaq (defensa en profundidad).
    key: 'obra-radar',
    name: 'Obra Radar',
    category: 'operaciones',
    // Obra Radar detecta obras nuevas para ir a venderles: es CAPTACIÓN, no
    // operación. Mezclado con Correo y Documentos se leía como una tarea
    // administrativa más.
    byArchetype: { 'service-pro': { name: 'Obra Radar', category: 'captacion' } },
    icon: <Radar size={size} />,
    backendModuleKey: 'obra-radar',
    sidebarItems: [
      { tab: 'nacional', label: 'Radar nacional', icon: <Radar size={size} /> },
    ],
  },

  {
    // Atención Ciudadana (B2G · gobierno). Mapa de calor + KPIs de reportes.
    // Gateado: backendModuleKey 'citizen-reports' (enabled_modules del municipio).
    // El endpoint filtra por org_id del JWT → cada municipio ve solo lo suyo.
    key: 'citizen-reports',
    name: 'Atención Ciudadana',
    category: 'operaciones',
    icon: <Building2 size={size} />,
    backendModuleKey: 'citizen-reports',
    sidebarItems: [
      { tab: 'dashboard', label: 'Inteligencia Urbana', icon: <MapPin size={size} /> },
      { tab: 'buzones', label: 'Tickets', icon: <Inbox size={size} /> },
    ],
  },

  {
    // Orion for Government · Salud · DIF (Ruta de la Salud, señales epidemiológicas,
    // canalizaciones y asistente médico con firma humana). OPT_IN_ONLY en Orión:
    // ningún arquetipo lo enciende; backend 404 sin GovHealthConfig.enabled.
    key: 'gov-health',
    name: 'Salud · DIF',
    category: 'operaciones',
    icon: <HeartPulse size={size} />,
    backendModuleKey: 'gov-health',
    sidebarItems: [
      { tab: 'panorama', label: 'Panorama', icon: <Activity size={size} /> },
      { tab: 'jornada', label: 'Jornada en vivo', icon: <ClipboardPlus size={size} /> },
      { tab: 'ruta', label: 'Ruta de la Salud', icon: <Route size={size} /> },
      { tab: 'senales', label: 'Señales', icon: <HeartPulse size={size} /> },
      { tab: 'canalizaciones', label: 'Canalizaciones', icon: <Send size={size} /> },
      { tab: 'asistente', label: 'Asistente médico', icon: <Stethoscope size={size} /> },
    ],
  },
  {
    // Orion for Government · Tablero del Presidente (vista ejecutiva entre
    // dependencias). OPT_IN_ONLY; backend exige gobierno:ejecutivo:read.
    key: 'gov-executive',
    name: 'Tablero del Presidente',
    category: 'operaciones',
    icon: <Landmark size={size} />,
    backendModuleKey: 'gov-executive',
    sidebarItems: [
      { tab: 'tablero', label: 'Tablero', icon: <Landmark size={size} /> },
      { tab: 'inteligencia', label: 'Inteligencia de medios', icon: <Radar size={size} /> },
    ],
  },

  {
    // Matriz de Buzones — vista de gobernanza solo-admin. Muestra, EN VIVO, qué
    // dependencia atiende cada buzón + responsable (identidad de Orión) + volumen,
    // y permite APROBAR la matriz dejando traza. Reusa el gate 'citizen-reports'
    // (ya encendido en municipio) + requireAdmin → los directores NO la ven, solo
    // el admin. Backend: GET/POST /citizen-reports/buzon-matrix.
    key: 'buzon-matrix',
    name: 'Buzones',
    category: 'operaciones',
    icon: <Grid3x3 size={size} />,
    backendModuleKey: 'citizen-reports',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'matriz', label: 'Matriz', icon: <Grid3x3 size={size} /> },
    ],
  },

  {
    // Contrato de Gobernanza Soberana — auditoría legal (solo-admin). Muestra, por
    // conversación, el contrato FIRMADO de lo que viaja al modelo soberano (ENVIADO
    // + RECIBIDO). Reusa el gate 'citizen-reports' (ya encendido en municipio) +
    // requireAdmin → los directores NO la ven. Backend: GET /governance/contracts.
    key: 'governance',
    name: 'Gobernanza',
    category: 'operaciones',
    icon: <ShieldCheck size={size} />,
    backendModuleKey: 'citizen-reports',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'contrato', label: 'Contrato', icon: <ShieldCheck size={size} /> },
    ],
  },

  {
    // Prompt del Agente — editor de la capa `base` del prompt (solo-admin). Editar
    // → Guardar crea una versión NUEVA y la activa al instante (efecto al siguiente
    // mensaje, sin desplegar); Historial + Restaurar = rollback a la versión
    // funcional. Reusa el gate 'citizen-reports' (ya encendido en municipio) +
    // requireAdmin → los directores NO lo ven, solo el admin. El backend
    // (Orión /prompts/org-base*) gatea admin/dev/cto y valida multi-tenant.
    key: 'agent-prompt',
    name: 'Prompt del Agente',
    category: 'operaciones',
    icon: <Wand2 size={size} />,
    backendModuleKey: 'citizen-reports',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'editor', label: 'Prompt', icon: <Wand2 size={size} /> },
    ],
  },

  {
    // 📱 WhatsApp como APP dentro de Orion (2026-10-22): bandeja de dos paneles
    // de los chats de WhatsApp de clientes, sugerencias del agente, modo de
    // respuesta (Automático / Solo sugiere / Apagado). Desde 22-oct reúne TODO lo
    // de WhatsApp: Chats · Difusión (contactos, plantillas, envíos masivos) ·
    // Números y conexión · Configuración (modo, bienvenida, perfil, catálogo, horario). Reemplaza a «Conversaciones» para
    // el canal WhatsApp: ahí se ocultan esos hilos cuando esta app está activa.
    // Gate POR DATO: el backend solo enciende `whatsapp_app` si la org tiene un
    // número de WhatsApp habilitado (y Conversaciones encendido); apagable por org.
    key: 'whatsapp-app',
    name: 'WhatsApp',
    category: 'operaciones',
    byArchetype: { 'healthcare-clinic': { name: 'WhatsApp', category: 'pacientes' }, 'events-services': { name: 'WhatsApp', category: 'clientes' }, 'service-pro': { name: 'WhatsApp', category: 'clientes' } },
    icon: <WhatsAppGlyph size={size} title={null} />,
    backendModuleKey: 'whatsapp_app',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'chats', label: 'Chats', icon: <MessageSquare size={size} /> },
      { tab: 'difusion', label: 'Difusión', icon: <Megaphone size={size} /> },
      { tab: 'numeros', label: 'Números y conexión', icon: <Phone size={size} /> },
      { tab: 'ajustes', label: 'Configuración', icon: <Settings size={size} /> },
    ],
  },

  {
    // Conversaciones — bandeja admin de TODAS las conversaciones de la org
    // (directivos ↔ agente + ciudadanos ↔ agente). Reusa ThreadsDesk mode="admin"
    // → GET /threads/ (el backend filtra por org_id del JWT y por rol: admin ve
    // todo). Gateado: backendModuleKey 'conversaciones' (enabled_modules; hoy
    // solo municipio-slp). Incluye lectura de mensajes + control humano (takeover).
    key: 'conversaciones',
    name: 'Conversaciones',
    category: 'operaciones',
    icon: <MessageSquare size={size} />,
    backendModuleKey: 'conversaciones',
    // Staff: solo admin (los directores ven SOLO sus tickets). Portal de socio:
    // las vendedoras entran y ven ÚNICAMENTE las conversaciones que un admin les
    // asignó — lo impone el servidor (/threads ata owner_user_id a la vendedora);
    // aquí solo se muestra el módulo. requireAdmin aplica al portal de staff.
    requireAdmin: true,
    audiences: ['staff', 'partner'],
    sidebarItems: [
      { tab: 'todas', label: 'Todas', partnerLabel: 'Mis conversaciones', icon: <MessageSquare size={size} /> },
    ],
  },

  {
    // Padrón de Establecimientos (Comercio · B2G). Semáforo de licencias /
    // funcionamiento por dependencia. Gateado en DOS ejes (modular/replicable):
    //   · backendModuleKey 'establishments' → per-org vía enabled_modules
    //     (hoy solo municipio-slp; cualquier org lo enciende sin tocar código).
    //   · requireRoles ['comercio'] → dentro de la org, SOLO el Director de
    //     Comercio (rol `comercio`) o un admin lo ven; los demás directores NO.
    // El candado REAL de datos lo pone el backend (scope establishments:read).
    key: 'establishments',
    name: 'Padrón',
    category: 'operaciones',
    icon: <Store size={size} />,
    backendModuleKey: 'establishments',
    requireRoles: ['comercio'],
    sidebarItems: [
      { tab: 'semaforo', label: 'Semáforo', icon: <Store size={size} /> },
    ],
  },

  {
    // Desarrollo Urbano · Territorio (B2G). Visión por computadora sobre imagen
    // satelital PÚBLICA (Sentinel-2, Meta/WRI dosel 1 m, ESA WorldCover, OSM):
    // pérdida de vegetación / nueva obra 2019→2026 con explicación por hotspot,
    // % de arbolado por colonia y demo de conteo árbol por árbol (DeepForest).
    // Demostración con capas precalculadas (public/gov-territory, sin PII).
    // Gate fail-closed: backendModuleKey 'gov-territory' (OPT_IN_ONLY en Orión,
    // enabled_modules por org; pensado para municipio-slp). Sin la key en
    // ALL_MODULES del backend no aparece. requireAdmin: aún no existe un rol de
    // capacidad de Desarrollo Urbano/Obras (como `comercio` o `salud`); cuando
    // exista, cambiar a requireRoles:[ese_rol] (admin conserva el bypass).
    // ⚠️ public/gov-territory/** es público aunque el módulo esté apagado
    // (datos satelitales y OSM abiertos, sin PII).
    key: 'gov-territory',
    name: 'Territorio',
    category: 'operaciones',
    icon: <Trees size={size} />,
    backendModuleKey: 'gov-territory',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'vegetacion', label: 'Vegetación y obra', icon: <Building2 size={size} /> },
      { tab: 'arbolado', label: 'Arbolado', icon: <Trees size={size} /> },
      { tab: 'metodo', label: 'Método y fuentes', icon: <BookOpen size={size} /> },
    ],
  },

  {
    // Orion for Government · Seguridad C4 (consola de alertas de video). Gate:
    // backendModuleKey 'gov-security' (default OFF en todo arquetipo → enabled_modules
    // por org) + requireRoles c4_* (admin/dev pasan). Datos: /admin/gov-security/*
    // (scope security:*; 404 si la org no tiene GovSecurityConfig.enabled).
    key: 'gov-security',
    name: 'Seguridad · C4',
    category: 'operaciones',
    icon: <Cctv size={size} />,
    backendModuleKey: 'gov-security',
    requireRoles: ['c4_operador', 'c4_supervisor'],
    sidebarItems: [
      { tab: 'consola', label: 'Consola de alertas', icon: <Cctv size={size} /> },
    ],
  },

  {
    // Orion for Government · Bienestar Animal (CIBA, depende de Seguridad). Gate:
    // backendModuleKey 'gov-animal' + rol `ciba` (admin/dev pasan). /admin/gov-animal/*.
    key: 'gov-animal',
    name: 'Bienestar Animal',
    category: 'operaciones',
    icon: <PawPrint size={size} />,
    backendModuleKey: 'gov-animal',
    requireRoles: ['ciba'],
    sidebarItems: [
      { tab: 'ciba', label: 'CIBA', icon: <PawPrint size={size} /> },
    ],
  },

  {
    // Inventario y Rentas (ERP núcleo, genérico). Equipos + rentas + cambios
    // de equipo + morosos + por vencer. Backend /admin/assets/* filtra por
    // org_id del JWT. Gateado: backendModuleKey 'rental-assets' en
    // enabled_modules (hoy encendido para rentalmaq).
    key: 'rental-assets',
    name: 'Inventario y Rentas',
    category: 'operaciones',
    // Home del arquetipo service-pro: se pinea arriba en "Principal", así que
    // la categoría casi no se usa — pero se declara `rentas` para que quede
    // en el grupo correcto si algún día deja de ser el home.
    byArchetype: {
      'service-pro': { name: 'Flota y Rentas', category: 'rentas' },
    },
    icon: <Package size={size} />,
    backendModuleKey: 'rental-assets',
    // Disponibilidad va PRIMERO: el operador arranca el día respondiendo
    // "¿tengo esta máquina libre?", no revisando el catálogo.
    sidebarItems: [
      { tab: 'disponibilidad', label: 'Disponibilidad', icon: <Calendar size={size} /> },
      { tab: 'rentas', label: 'Rentas', icon: <ClipboardList size={size} /> },
      { tab: 'inventario', label: 'Inventario', icon: <Package size={size} /> },
      { tab: 'bi', label: 'BI de flota', icon: <BarChart3 size={size} /> },
    ],
  },

  {
    // Client Radar — buscador cognitivo de leads gobernado por U-CogNet.
    // Disponible para CUALQUIER org (cada una ve solo sus leads vía X-Org-Id).
    // Gate de visibilidad: backendModuleKey 'client-radar' en enabled_modules
    // (hoy encendido para ucognet). El proxy de Orión reenvía el X-Org-Id.
    key: 'client-radar',
    name: 'Client Radar',
    category: 'operaciones',
    icon: <Satellite size={size} />,
    backendModuleKey: 'client-radar',
    sidebarItems: [
      { tab: 'leads', label: 'Radar de leads', icon: <Satellite size={size} /> },
    ],
  },

  {
    // Memoria gobernada del cerebro soberano (U-CogNet). Grafo auditable +
    // corrección append-only. Gateado por org (backendModuleKey) — encendido
    // hoy para municipio-slp. El proxy de Orión deriva org/clearance del JWT.
    key: 'memory-graph',
    name: 'Memoria del agente',
    category: 'operaciones',
    icon: <Brain size={size} />,
    backendModuleKey: 'memory-graph',
    requireAdmin: true,   // solo admin: los directores ven SOLO sus tickets
    sidebarItems: [
      { tab: 'graph', label: 'Memoria gobernada', icon: <Brain size={size} /> },
    ],
  },

  {
    // Consolidación offline — revisión humana de la memoria gobernada (U-CogNet):
    // fusiones propuestas + conflictos detectados, con veredicto experto. Proxy
    // /consolidation/* al cerebro soberano (token + reviewer server-side, org del
    // JWT). Gateado por backendModuleKey 'consolidation' (hoy municipio-slp).
    key: 'consolidation',
    name: 'Consolidación',
    category: 'operaciones',
    icon: <GitMerge size={size} />,
    backendModuleKey: 'consolidation',
    requireAdmin: true,   // solo admin: los directores ven SOLO sus tickets
    sidebarItems: [
      { tab: 'review', label: 'Revisión', icon: <ListChecks size={size} /> },
    ],
  },

  {
    // WhatsApp Console — herramienta interna (solo superuser) para grabar los
    // videos de Meta App Review. Envía mensajes y gestiona plantillas desde un
    // número de PRUEBA de Meta ya conectado a la app Orión. No es para clientes.
    key: 'whatsapp-console',
    name: 'WhatsApp Console',
    category: 'dev',
    icon: <MessageSquare size={size} />,
    // Gate a cto/developer (el dueño los tiene; clientes no). NO requireSuperuser:
    // el JWT de login trae role[] pero NO is_superuser, así que ese gate lo ocultaría.
    devOnly: true,
    sidebarItems: [
      { tab: 'console', label: 'WhatsApp Console', icon: <MessageSquare size={size} /> },
    ],
  },

  {
    // Futuria · Mi Plantel: la vista del ORIENTADOR sobre su generación (quién
    // va a medias, distribución, evidencias). Es el otro lado de university-bi,
    // que mira demanda de carreras. El aislamiento por escuela NO vive en el
    // front: viaja como rol `plantel:<nombre>` en el JWT y lo aplica Django.
    key: 'futuria-plantel',
    name: 'Mi Plantel',
    category: 'operaciones',
    byArchetype: { 'educational-institution': { name: 'Mi Plantel', category: 'operaciones' } },
    icon: <Users size={size} />,
    backendModuleKey: 'futuria-plantel',
    sidebarItems: [
      { tab: 'panel', label: 'Mi generación', icon: <Users size={size} /> },
    ],
  },

  {
    // Futuria · Inteligencia para Universidades (BI B2B). Gateado:
    // backendModuleKey 'university-bi' (solo en enabled_modules de futuria).
    key: 'university-bi',
    name: 'Inteligencia Universidades',
    category: 'operaciones',
    byArchetype: { 'educational-institution': { name: 'Inteligencia Universidades', category: 'operaciones' } },
    icon: <GraduationCap size={size} />,
    backendModuleKey: 'university-bi',
    sidebarItems: [
      { tab: 'panel', label: 'Panel de inteligencia', icon: <GraduationCap size={size} /> },
    ],
  },

  {
    // Admin God-mode de la matriz de módulos (homologación). Gateado por
    // backendModuleKey 'module-admin' (habilitado solo en ucognet); los endpoints
    // exigen thot_admin/superuser. Reemplaza el rol de Thot (que va a desaparecer).
    key: 'module-admin',
    name: 'Admin de Módulos',
    category: 'config',
    icon: <SlidersHorizontal size={size} />,
    backendModuleKey: 'module-admin',
    sidebarItems: [
      { tab: 'matrix', label: 'Matriz de módulos', icon: <SlidersHorizontal size={size} /> },
    ],
  },

  // ═══ Expo Capture — Economic Genesis Layer Phase 0 (NABU-56) ══════
  // Captura de proveedores en eventos físicos + Vision Supplier Extractor.
  // Bow-tie: backend EXPO_CAPTURE_ENABLED feature flag.
  {
    key: 'expo-capture',
    name: 'Expo Capture',
    category: 'operaciones',
    icon: <Camera size={size} />,
    backendModuleKey: 'expo_capture',
    sidebarItems: [
      { tab: 'list', label: 'Capturas', icon: <ClipboardList size={size} /> },
      { tab: 'create', label: 'Capturar', icon: <Plus size={size} /> },
    ],
  },

  // ═══ Quetzal — Tourism Intelligence Phase 0 (NABU-66) ══════════════
  // Foundation: track events + experiences + local businesses + tourists.
  // Privacy-preserving aggregations via tourist_segment_hash (SHA256
  // rotativo). Bow-tie: backend QUETZAL_TRACKING_ENABLED feature flag.
  {
    key: 'quetzal-core',
    name: 'Quetzal · Turismo',
    category: 'operaciones',
    icon: <Plane size={size} />,
    backendModuleKey: 'trips',
    sidebarItems: [
      { tab: 'experiences', label: 'Experiencias', icon: <Star size={size} /> },
      { tab: 'businesses', label: 'Comercio local', icon: <Building2 size={size} /> },
      { tab: 'tourists', label: 'Tourists', icon: <Users size={size} /> },
    ],
  },

  // ═══ Quotes — Cotizaciones desde lead (NABU-60) ════════════════════
  // FK opcional a sales.Lead, plantillas reutilizables, PDF server-side,
  // tracking de estado (draft → sent → viewed → accepted/rejected/expired)
  // y link público shareable (/q/{token}/) sin auth para feedback de cliente.
  {
    key: 'quotes',
    name: 'Cotizaciones',
    category: 'ventas',
    byArchetype: { 'service-pro': { name: 'Cotizaciones', category: 'rentas' } },
    icon: <FileText size={size} />,
    // Llave propia. Antes colgaba de `trips`, que sólo está habilitada para
    // agency-travel: cotizar no es una función de agencia de viajes, y ese
    // gate dejaba Cotizaciones INVISIBLE para cualquier otra vertical —
    // incluida una rentadora, donde la cotización es el paso previo a rentar.
    backendModuleKey: 'quotes',
    sidebarItems: [
      { tab: 'list', label: 'Lista', icon: <ClipboardList size={size} /> },
      { tab: 'create', label: 'Crear', icon: <Plus size={size} /> },
      { tab: 'templates', label: 'Plantillas', icon: <LayoutTemplate size={size} /> },
    ],
  },

  // ═══ Invoicing — facturas, pagos, AR dashboard (NABU-61) ════════
  // Cierra el funnel lead → quote → invoice → pago. Backend: apps/invoicing/.
  // Bow-tie: módulo greenfield aditivo; rollback = revert + drop tablas.
  {
    key: 'invoicing',
    name: 'Facturación',
    category: 'ventas',
    byArchetype: { 'healthcare-clinic': { name: 'Facturación', category: 'administracion' }, 'events-services': { name: 'Facturación', category: 'administracion' } , 'service-pro': { name: 'Facturación', category: 'rentas' } },
    icon: <FileText size={size} />,
    backendModuleKey: 'invoicing',
    sidebarItems: [
      { tab: 'dashboard', label: 'Dashboard MTD/AR', icon: <BarChart3 size={size} /> },
      { tab: 'list', label: 'Facturas', icon: <ClipboardList size={size} /> },
      { tab: 'create', label: 'Nueva', icon: <Plus size={size} /> },
    ],
  },

  // ═══ Ventas (TKT-00223) ═══════════════════════════════════════
  // Lo que la org cobró con Stripe (eventos, reservas, expositores) con la
  // factura pendiente a la vista. Solo admins: el backend exige `cobros:read`
  // (wildcard de admin/cto). Backend: apps/invoicing → módulo `invoicing`.
  {
    key: 'ventas',
    name: 'Ventas',
    category: 'ventas',
    byArchetype: { 'healthcare-clinic': { name: 'Ventas', category: 'administracion' }, 'events-services': { name: 'Ventas', category: 'administracion' }, 'service-pro': { name: 'Ventas', category: 'administracion' } },
    icon: <BadgeDollarSign size={size} />,
    backendModuleKey: 'invoicing',
    requireAdmin: true,
    sidebarItems: [
      { tab: 'cobros', label: 'Cobros', icon: <BadgeDollarSign size={size} />, requireAdmin: true },
    ],
  },

  // ═══ Operación (apps/workforce) ══════════════════════════════════
  // La cara del administrador sobre el piso: quién llegó, qué se reportó,
  // qué falta aprobar. El trabajador marca desde su teléfono; aquí se ve.
  {
    key: 'workforce',
    name: 'Operación',
    category: 'rh',
    icon: <UserCheck size={size} />,
    backendModuleKey: 'workforce',
    sidebarItems: [
      { tab: 'asistencia', label: 'Asistencia', icon: <UserCheck size={size} /> },
      { tab: 'checklists', label: 'Checklists', icon: <ClipboardCheck size={size} /> },
      { tab: 'incidencias', label: 'Incidencias', icon: <AlertOctagon size={size} /> },
      { tab: 'tareas', label: 'Tareas', icon: <ListTodo size={size} /> },
      { tab: 'ausencias', label: 'Ausencias', icon: <CalendarDays size={size} /> },
    ],
  },

  // ═══ Inventario (apps/inventory) ═════════════════════════════════
  // Existencias por lote con PEPS, traslados con doble confirmación,
  // mermas justificadas y solicitudes automáticas. Genérico: cualquier org
  // que mueva insumos entre puntos lo usa.
  {
    key: 'inventory',
    name: 'Inventario',
    category: 'operaciones',
    icon: <Boxes size={size} />,
    backendModuleKey: 'inventory',
    sidebarItems: [
      { tab: 'existencias', label: 'Existencias', icon: <Boxes size={size} /> },
      { tab: 'caducar', label: 'Por caducar', icon: <CalendarClock size={size} /> },
      { tab: 'traslados', label: 'Traslados', icon: <ArrowLeftRight size={size} /> },
      { tab: 'solicitudes', label: 'Solicitudes', icon: <ClipboardList size={size} /> },
    ],
  },

  // ═══ POS — mostrador (apps/pos + apps/locations) ═════════════════
  // Ventana del ADMINISTRADOR sobre el mostrador: qué se vendió, dónde, qué
  // se corrigió y cómo cerró la caja. El cobro en sí NO vive aquí — eso es
  // la app del punto de venta, otra superficie con otras exigencias
  // (offline, pantalla grande, dos toques).
  //
  // Genérico: cualquier org que cobre en mostrador lo usa. Nada atado a un
  // cliente ni a un giro.
  {
    key: 'pos',
    name: 'Mostrador',
    category: 'operaciones',
    icon: <Store size={size} />,
    backendModuleKey: 'pos',
    sidebarItems: [
      { tab: 'panel', label: 'Panel de control', icon: <Gauge size={size} /> },
      { tab: 'ventas', label: 'Ventas del día', icon: <ShoppingCart size={size} /> },
      { tab: 'cierres', label: 'Cierres y cortes', icon: <ClipboardList size={size} /> },
      { tab: 'catalogo', label: 'Catálogo', icon: <Tag size={size} /> },
      { tab: 'puntos', label: 'Puntos', icon: <MapPin size={size} /> },
    ],
  },

  // ═══ Expenses — Thoth Expenses v0.1 (NABU-63) ════════════════════
  // Tracking de cuentas-por-pagar con categorías, recibos y dashboard
  // gastos vs invoicing. Bow-tie: módulo greenfield, sin downstream.
  {
    key: 'expenses',
    name: 'Gastos',
    category: 'operaciones',
    icon: <Receipt size={size} />,
    // Tenía `trips` — la misma llave que Quetzal·Turismo, Deals,
    // Leads·Importar y Stakeholders. Encender Gastos para un negocio de
    // comida le prendía cuatro módulos de agencia de viajes. Llave propia.
    backendModuleKey: 'expenses',
    sidebarItems: [
      { tab: 'list', label: 'Lista', icon: <ClipboardList size={size} /> },
      { tab: 'create', label: 'Registrar', icon: <Plus size={size} /> },
      { tab: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={size} /> },
    ],
  },

  // ═══ Deals — Pipeline visual de ventas (NABU-67) ════════════════════
  // Kanban con stages configurables, drag-drop para mover etapas, panel
  // de forecast (weighted + cohort por mes). Wired-in append-only con
  // sales.Lead.qualified → deals.create automático (vía signal).
  {
    key: 'sales-desk',
    name: 'Ventas y Comisiones',
    category: 'ventas',
    icon: <BadgeDollarSign size={size} />,
    // staff (admin ve al equipo) + partner (el socio/vendedor ve SOLO lo suyo,
    // forzado por servidor en apps/commissions). Portal de socios = esto.
    audiences: ['staff', 'partner'],
    backendModuleKey: 'sales-desk',
    sidebarItems: [
      { tab: 'comisiones', label: 'Comisiones', icon: <BadgeDollarSign size={size} /> },
      { tab: 'ventas', label: 'Ventas', icon: <ShoppingCart size={size} /> },
      { tab: 'reglas', label: 'Reglas', icon: <Percent size={size} />, requireAdmin: true },
    ],
    byArchetype: {
      'agency-travel': {
        sidebarItems: { ventas: { label: 'Reservas vendidas' } },
      },
    },
  },
  {
    key: 'deals',
    name: 'Deals · Pipeline',
    category: 'ventas',
    icon: <LineChart size={size} />,
    backendModuleKey: 'trips',
    sidebarItems: [
      { tab: 'kanban', label: 'Pipeline', icon: <LayoutTemplate size={size} /> },
      { tab: 'forecast', label: 'Forecast', icon: <BarChart3 size={size} /> },
      { tab: 'create', label: 'Crear', icon: <Plus size={size} /> },
    ],
  },

  // ═══ Leads Import — CSV bulk + Orion mapping + auto presentación + landing ═══
  // Pipeline: subir CSV cualquier estructura → Orion mapea → bulk Lead create
  // → enrichment + presentación + solution_landing pública por cada lead.
  // Bow-tie: LEADS_IMPORT_ENABLED feature flag (off → endpoints 404).
  {
    key: 'leads-import',
    name: 'Leads · Importar',
    category: 'ventas',
    icon: <Upload size={size} />,
    backendModuleKey: 'trips',
    sidebarItems: [
      { tab: 'upload', label: 'Subir CSV', icon: <Upload size={size} /> },
      { tab: 'crm', label: 'CRM (todos los leads)', icon: <UserCheck size={size} /> },
      { tab: 'landings', label: 'Landings & URLs', icon: <ListChecks size={size} /> },
    ],
  },

  // ═══ AV Producer — Orión productor audiovisual (Sevilla Palace pattern) ═
  // Sube fotos por escena, vision auto-corre, Kling v2 Pro anima, ffmpeg
  // concatena. Multi-tenant: cada org produce sus videos sin tocar otras.
  {
    key: 'av-producer',
    name: 'Productor · Audiovisual',
    category: 'marketing',
    icon: <Film size={size} />,
    backendModuleKey: 'av_producer',
    sidebarItems: [
      { tab: 'studio', label: 'Estudio (Orión)', icon: <Film size={size} /> },
    ],
  },

  // ═══ Public Tracking — KPIs del visitante público (multi-tenant) ═══
  // Universal: cualquier sitio público (calaita.mx, theadventure.mx,
  // mimercadito.click, futuras orgs) registra visitor + events sin auth.
  // El admin ve unique visitors, top pages, funnel drop-off, timelines.
  {
    key: 'public-tracking',
    name: 'Visitas públicas',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Tráfico del sitio', category: 'captacion' }, 'events-services': { name: 'Tráfico del sitio', category: 'captacion' } },
    icon: <Users size={size} />,
    backendModuleKey: 'public_tracking',
    sidebarItems: [
      { tab: 'visitors', label: 'Visitantes y funnel', icon: <Users size={size} /> },
      { tab: 'campana', label: 'Resultados de campaña', icon: <TrendingUp size={size} /> },
    ],
  },

  // ═══ Pipeline Constructor — B2B prospecting obras públicas ══════
  // Scrapea Compranet/LOPSRM weekly; score por inventory + geo + urgency;
  // multitenant via JWT org_id. Sin proxy, sin costo recurrente.
  {
    key: 'pipeline-constructor',
    name: 'Pipeline Constructor',
    category: 'ventas',
    icon: <Briefcase size={size} />,
    backendModuleKey: 'pipeline_constructor',
    sidebarItems: [
      { tab: 'leads', label: 'Leads', icon: <ListChecks size={size} /> },
      { tab: 'inventory', label: 'Inventario', icon: <Package size={size} /> },
      { tab: 'runs', label: 'Corridas', icon: <Activity size={size} /> },
    ],
  },

  // ═══ Lead Magnets — captura upstream + sequences (NABU-69) ══════
  // Landing pública gated → email sequences → handoff append-only a Leads.
  // Bow-tie: LEAD_MAGNETS_ENABLED feature flag (off → /public/lm/* 404).
  {
    key: 'lead-magnets',
    name: 'Lead Magnets',
    category: 'marketing',
    byArchetype: { 'healthcare-clinic': { name: 'Captación de pacientes', category: 'captacion' }, 'events-services': { name: 'Captación de clientes', category: 'captacion' } },
    icon: <Inbox size={size} />,
    backendModuleKey: 'lead_magnets',
    sidebarItems: [
      { tab: 'list', label: 'Lead Magnets', icon: <ClipboardList size={size} /> },
      { tab: 'create', label: 'Crear magnet', icon: <Plus size={size} /> },
    ],
  },

  // ═══ Projects — quote aceptado → milestones → invoice (NABU-62) ══════
  // Cierra el hueco operacional entre sales/quotes e invoicing para
  // servicios profesionales (signals PyME 7.72 + 7.83). Append-only,
  // sin tocar schemas existentes. Ver bow-tie en backend PR.
  {
    key: 'projects',
    name: 'Proyectos',
    category: 'operaciones',
    // En renta de equipo para construcción, un proyecto ES una obra: así lo
    // nombra el cliente al pedir la máquina y así lo dice el equipo de campo.
    byArchetype: { 'service-pro': { name: 'Obras', category: 'operaciones' } },
    icon: <Briefcase size={size} />,
    backendModuleKey: 'time_tracking',
    sidebarItems: [
      { tab: 'list', label: 'Lista', icon: <ClipboardList size={size} /> },
      { tab: 'time-entries', label: 'Horas', icon: <Clock size={size} /> },
    ],
  },

  // ═══ QUETZAL Stakeholder Graph + Opportunity Scanner (NABU-73) ════
  // Universo non-tourist Mundial 2026 + scanner de gaps emergentes.
  // Bow-tie: backend QUETZAL_OPPORTUNITY_SCANNER_ENABLED feature flag.
  {
    key: 'quetzal-stakeholder-graph-oppo',
    name: 'QUETZAL · Stakeholders',
    category: 'operaciones',
    icon: <Globe size={size} />,
    backendModuleKey: 'trips',
    sidebarItems: [
      { tab: 'stakeholders', label: 'Stakeholders', icon: <Users size={size} /> },
      { tab: 'stakeholder-create', label: 'Registrar', icon: <Plus size={size} /> },
      { tab: 'opportunities', label: 'Opportunity Gaps', icon: <Activity size={size} /> },
      { tab: 'opportunity-create', label: 'Crear gap', icon: <Plus size={size} /> },
    ],
  },

  // ═══ Agentes — básico de TODA org (2026-10-28) ═════════════════════
  // En el shell Orion vive en `orion/agentes`; este módulo es la MISMA app para
  // el shell clásico (por eso requireShell 'classic': nunca se duplica). Gate
  // tolerante con `assistant` (DEFAULT_ON en Orión): solo se oculta si la org
  // apagó los agentes a propósito.
  {
    key: 'agentes',
    name: 'Agentes',
    category: 'operaciones',
    icon: <Bot size={size} />,
    backendModuleKey: null,
    softBackendModuleKey: 'assistant',
    requireShell: 'classic',
    sidebarItems: [
      { tab: 'mis-agentes', label: 'Mis agentes', icon: <Bot size={size} /> },
    ],
  },

  // ═══ Configuración (Ajustes) — para TODO usuario (2026-10-28) ════════
  // Mi perfil (puesto, acerca de mí, cómo quiere que le hable la IA) para cualquiera;
  // la parte de la organización (prompt de la org, agente IA, módulos) la ve solo
  // un admin DENTRO de la página — sin requireAdmin aquí porque ModuleRoute lo
  // evalúa antes de que cargue el rol y rebotaría al admin. El backend exige admin.
  // En el shell Orion es la entrada fija «Configuración» al fondo de la barra.
  {
    key: 'ajustes',
    name: 'Ajustes',
    category: 'config',
    icon: <Settings size={size} />,
    backendModuleKey: null,
    sidebarItems: [
      { tab: 'inicio', label: 'Configuración', icon: <Settings size={size} /> },
    ],
  },

  // ═══ Shell «Orion» — Inicio + Asistente + Agentes + Cambios del sitio ══
  // SOLO existe para orgs que activaron el shell por DATOS
  // (custom_overrides.dashboard.shell = "orion"; ver DashboardShellContext).
  // `requireShell` lo oculta para cualquier otra org → cero cambios fuera.
  {
    key: 'orion',
    name: 'Orion',
    category: 'panel',
    icon: <Sparkles size={size} />,
    backendModuleKey: null,
    requireShell: 'orion',
    sidebarItems: [
      { tab: 'inicio', label: 'Inicio', icon: <BarChart3 size={size} /> },
      { tab: 'asistente', label: 'Asistente', icon: <MessageSquare size={size} /> },
      { tab: 'agentes', label: 'Agentes', icon: <Bot size={size} /> },
      // Google Calendar: no en orgs soberanas (servicio extranjero).
      { tab: 'calendario', label: 'Calendario', icon: <CalendarDays size={size} />, hideForSovereign: true },
      // Sin requireAdmin a propósito: ModuleRoute lo evalúa antes de que cargue el rol
      // y rebotaría al admin. Aprobar/rechazar lo exige el backend (humano admin) y
      // la página oculta esas acciones a quien no es admin.
      { tab: 'cambios-sitio', label: 'Cambios del sitio', icon: <Globe size={size} /> },
      // Editor visual del sitio; la entrada «Sitio web» del shell lo abre solo si
      // la org tiene el módulo `site` (site_config) — ver nav.tsx.
      { tab: 'editor-sitio', label: 'Editor del sitio', icon: <Eye size={size} /> },
      // Tickets + evidencia automática (palabras literales, pantalla, medios). Igual
      // que «Cambios del sitio»: sin requireAdmin aquí; la entrada del menú es adminOnly.
      { tab: 'tickets', label: 'Tickets', icon: <ClipboardList size={size} /> },
    ],
  },

  // ═══ Gasto por org — cuánto le cuesta cada org a la plataforma ══════
  // Solo el dueño: el menú se muestra a quien tiene rol thot_admin y el servidor
  // (Orión /superadmin/costos) además exige entrar desde brainstream/ucognet.
  {
    key: 'costos',
    name: 'Gasto por org',
    category: 'config',
    icon: <CircleDollarSign size={size} />,
    backendModuleKey: null,
    requireRoles: ['thot_admin'],
    // El servidor exige el rol literal (y org de plataforma): sin pase de admin.
    requireRolesStrict: true,
    sidebarItems: [
      { tab: 'resumen', label: 'Gasto por org', icon: <CircleDollarSign size={size} /> },
    ],
  },

  // ═══ Multi-Org — god-mode cross-org panel (solo is_superuser=true) ══
  // Server-side enforcement: apps/superadmin/auth.py:thot_admin_auth.
  // Client-side gating: requireSuperuser (sale del switcher si JWT no tiene
  // is_superuser=true).
  {
    key: 'multi-org',
    name: 'Multi-Org · god-mode',
    category: 'config',
    icon: <Shield size={size} />,
    backendModuleKey: 'multi_org',
    requireSuperuser: true,
    sidebarItems: [
      { tab: 'orgs', label: 'Organizaciones', icon: <Building2 size={size} /> },
    ],
  },
];

// ── Lookups ───────────────────────────────────────────────────

export function findModule(key: string): ModuleDefinition | undefined {
  return moduleRegistry.find((m) => m.key === key);
}

export function findItem(
  moduleKey: string,
  tab: string,
): { module: ModuleDefinition; item: ModuleDefinition['sidebarItems'][number] } | null {
  const module = findModule(moduleKey);
  if (!module) return null;
  const item = module.sidebarItems.find((s) => s.tab === tab);
  if (!item) return null;
  return { module, item };
}

/** First sidebar tab of a module — the default landing when navigating to
 *  a bare `/m/:moduleKey`. Returns undefined if the module has no tabs. */
export function defaultTabOf(moduleKey: string): string | undefined {
  return findModule(moduleKey)?.sidebarItems[0]?.tab;
}
